
import {EventEmitter, Injectable, Output } from '@angular/core';
import {Builder} from '../model/builder';
import {Community} from '../model/community';
import {Model} from '../model/model';
import {Tour} from '../model/tour';
import {TourService} from './tour.service';
import {Scene} from '../model/scene';
import {OptionGroup} from '../model/option-group';
import {AboutInfo} from '../model/about-info';
import {CommunityInfo} from '../model/community-info';
import {ContactInfo} from '../model/contact-info';
import {EnumAware} from '../decorators/enum-aware.decorator';
import {Image, ImageSize} from '../model/image';
import {SurveyApiService} from './survey-api.service';
import {FeedbackInfo} from '../model/feedback-info';
import {GoTourSettings} from '../settings/gotour-settings';
import {HttpClient, HttpHeaders} from '@angular/common/http';

// const apiHost = 'http://gotour.api.avidratings.com/v1.0/';  // http://gotour.api.avidratings.com/v1.0/
// const tourHost = 'http://gotourhd.com/';

@EnumAware
@Injectable()
export class TourApiService {

  alias = '';
  builderPath = '';
  modelPath = '';
  builderList: Builder[] = [];
  private goTourSettings = new GoTourSettings().currentSettings();

  startDate;
  @Output() tourLoaded = new EventEmitter<boolean>();
  @Output() buildersLoaded = new EventEmitter<boolean>();
  @Output() sceneOptionsLoaded = new EventEmitter<boolean>();

  constructor(private http: HttpClient, private tourService: TourService, private goSurveyApiService: SurveyApiService) {}

  fetchTourInfoForAlias(alias: string) {
    this.alias = alias;
    if (this.builderList.length === 0) {
      this.fetchBuilderCommunityModelsList();
    }
  }

  fetchTourInfoForBuilderAndModel(builder: string, model: string) {
    // this.startDate = new Date();
    this.builderPath = builder;
    this.modelPath = model;
    this.fetchMainTourInfo().subscribe(
      data => {
        this.tourService.configureDisplayForTour(new Tour(data['results']));
        this.fetchExtraTourInfo();
        for (let i = 0; i < this.tourService.tour.sceneList.length; i++) {
          const scene: Scene = this.tourService.tour.sceneList[i];
          if (scene.showOptions) {
            this.fetchOptionsForScene(scene).subscribe(
              sceneData => {
                // some scenes may not have any options groups or options
                const optionGroupsListJSON: any[] = sceneData['results']['scene']['optionGroups'];
                for (const key in Object.keys(optionGroupsListJSON)) {
                  if (optionGroupsListJSON.hasOwnProperty(key)) {
                    scene.optionGroups.push(new OptionGroup(optionGroupsListJSON[key], scene.id));
                  }
                }
                this.sceneOptionsLoaded.emit(true);
                this.tourService.optionsAvailable = true;
              },
              error => {
                console.log(error);
              }
            );
          }
        }
      },
      error => {
        console.log(error);
      }
    );
  }

  fetchMainTourInfoForSharedWishList(builder: string, model: string) {
    this.builderPath = builder;
    this.modelPath = model;
    return this.fetchMainTourInfo();
  }

  fetchBuilderCommunityModelsList() {
    this.http.get(this.goTourAPIBaseWithVersion() + '/main/builders/communities/models', {
      headers: this.apiHeaders()
    }).subscribe(
        data => {

          if (data != null) {
            for (const b of data['results']['builders']) {
              this.builderList.push(new Builder(b));
            }
            this.buildersLoaded.emit(true);
            // load the tour for this alias
            if (this.alias !== '' && this.builderPath === '' && this.modelPath === '') {
              const m: Model = this.findModelForAlias(this.alias);
              if (m !== null) {
                this.builderPath = m.builderDirectory;
                this.modelPath = m.modelDirectory;
                this.fetchTourInfoForBuilderAndModel(this.builderPath, this.modelPath)
              }
            }
          }
        },
        error => {
          console.log(error);
        }
    );
  }

  fetchMainTourInfo() {
    return this.http.get(this.apiBaseWithBuilderModelsPath() + '/main', {
      headers: this.apiHeaders()
    });
  }

  fetchOptionsForScene(scene: Scene) {
    return this.http.get(this.apiBaseWithBuilderModelsPath() + '/scenes/' + scene.id + '/options', {
      headers: this.apiHeaders()
    });
  }

  fetchProductReviews(optionId: number, queryParams) {
    let productReviewsURL = this.apiBaseWithBuilderModelsPath() + '/options/' + optionId + '/reviews';
    if (queryParams != null) {
      productReviewsURL += '?' + queryParams;
    }
    return this.http.get(productReviewsURL, {
      headers: this.apiHeaders()
    });
  }

  fetchExtraTourInfo() {
    // About
    if (this.tourService.tour.features.about.enabled) {
      this.fetchAboutInfo().subscribe(
        data => {
          if (data['results']['about']['leftContent']) {
            this.tourService.tour.aboutHomeInfo = new AboutInfo(data['results']);
            this.tourService.tour.features.about.downloaded = true;
            // something went wrong - either the download failed or the permissions
            // and data for the tour (most likely) don't match
          } else {
            this.tourService.tour.features.about.enabled = false;
          }
          this.verifyContentDownloads();
        },
        error => {
          console.log(error);
        }
      );
    }

    // Community
    if (this.tourService.tour.features.community.enabled) {
      this.fetchCommunityInfo().subscribe(
        data => {
          if (data['results']['community']['mainView']) {
            this.tourService.tour.communityInfo = new CommunityInfo(data['results']);
            this.tourService.tour.features.community.downloaded = true;
            // something went wrong - either the download failed or the permissions
            // and data for the tour (most likely) don't match
          } else {
            this.tourService.tour.features.community.enabled = false;
          }
          this.verifyContentDownloads();
        },
        error => {
          console.log(error);
        }
      );
    }

    // Contacts
    if (this.tourService.tour.features.contacts.enabled) {
      this.fetchContactInfo().subscribe(
        data => {
          if (data['results']['contacts']['sections']) {
            this.tourService.tour.contactInfo = new ContactInfo(data['results']);
            this.tourService.tour.features.contacts.downloaded = true;
            // something went wrong - either the download failed or the permissions
            // and data for the tour (most likely) don't match
          } else {
            this.tourService.tour.features.contacts.enabled = false;
          }
          this.verifyContentDownloads();
        },
        error => {
          console.log(error);
        }
      );
    }

    // Feedback
    if (this.tourService.tour.features.feedback.enabled) {
      this.fetchFeedbackInfo().subscribe(
        data => {
          if (data['results']['ratings']['avidVerified']) {
            this.tourService.tour.feedbackInfo = new FeedbackInfo(data['results']);
            this.tourService.tour.features.feedback.downloaded = true;
            // something went wrong - either the download failed or the permissions
            // and data for the tour (most likely) don't match
          } else {
            this.tourService.tour.features.feedback.enabled = false;
          }
          this.verifyContentDownloads();
        },
        error => {
          console.log(error);
        }
      );
      this.fetchSurveyInfo();
    } else {
      this.goSurveyApiService.reviewsOverview = null;
    }

    // possible non of the above are enabled
    this.verifyContentDownloads();
  }

  fetchAboutInfo() {
    return this.http.get(this.apiBaseWithBuilderModelsPath() + '/about', {
      headers: this.apiHeaders()
    });
  }

  fetchCommunityInfo() {
    return this.http.get(this.apiBaseWithBuilderModelsPath() + '/community', {
      headers: this.apiHeaders()
    });
  }

  fetchContactInfo() {
    return this.http.get(this.apiBaseWithBuilderModelsPath() + '/contacts', {
      headers: this.apiHeaders()
    });
  }

  fetchFeedbackInfo() {
    return this.http.get(this.apiBaseWithBuilderModelsPath() + '/ratings', {
      headers: this.apiHeaders()
    });
  }

  fetchSurveyInfo() {
    // we need the builders list so we can find the public review id
    if (this.builderList.length === 0) {
      this.buildersLoaded.subscribe(
        (loaded: boolean) => {
          if (loaded) {
            this.goSurveyApiService.fetchSummaryAndReviews(this.findPublicReviewId());
          }
        }
      );
      this.fetchBuilderCommunityModelsList();
    } else {
      this.goSurveyApiService.fetchSummaryAndReviews(this.findPublicReviewId());
    }
  }

  verifyContentDownloads() {
    let downloadComplete = true;
    if (this.tourService.tour.features.about.enabled
        && !this.tourService.tour.features.about.downloaded) {
      downloadComplete = false;
    }
    if (this.tourService.tour.features.community.enabled
      && !this.tourService.tour.features.community.downloaded) {
      downloadComplete = false;
    }
    if (this.tourService.tour.features.contacts.enabled
      && !this.tourService.tour.features.contacts.downloaded) {
      downloadComplete = false;
    }
    if (this.tourService.tour.features.feedback.enabled
      && !this.tourService.tour.features.feedback.downloaded) {
      downloadComplete = false;
    }

    if (downloadComplete) {
      const date = new Date();
      // OLD: 761, 685, 833, 830, 816 ms  - 785 avg
      // OLD VPN: 1462, 2028, 2098, 1760, 2609 - 1991
      // NEW : 3632, 3148, 2714, 4159, 4075 ms - 3134 avg
      // console.log('load time: ' + (date.getTime() - this.startDate.getTime()));
      this.tourLoaded.emit(true);
      if (this.tourService.tour.hasVideoTours()) {
        this.tourService.calculateBandwidth(this.tourService.tour.tourVideo.fullFrameEncodings);
      }
    }
  }

  tourRoutesForContent(): string[] {
    return [this.builderPath, this.modelPath].concat(
      (this.tourService.tour.isWeb360Only() ? ['tour', '3d'] : ['tour', 'video'])
    );
  }

  findModelForAlias(alias: string) {
    for (const b of this.builderList) {
      for (const c of (b as Builder).communities) {
        for (const m of (c as Community).models) {
          if (m.alias === alias) {
            return m;
          }
        }
      }
    }
    return null;
  }

  findPublicReviewId() {
    for (const b of this.builderList) {
      for (const c of (b as Builder).communities) {
        for (const m of (c as Community).models) {
          if (m.builderDirectory === this.builderPath && m.modelDirectory === this.modelPath) {
            return m.publicReviewId;
          }
        }
      }
    }
    return null;
  }

  imageLibraryURL(image: Image, preferredSize: ImageSize) {
    if (image) {
      if (!image.originalURL) {
        image.originalURL = this.goTourResourceWithBuilderModelPath() + '/' +  image.original;
      }
      if (!image.mediumURL && image.optimizedMedium != null) {
        image.mediumURL = this.goTourResourceWithBuilderModelPath() + '/' + image.optimizedMedium;
      }
      if (!image.largeURL && image.optimizedLarge != null) {
        image.largeURL = this.goTourResourceWithBuilderModelPath() + '/' + image.optimizedLarge;
      }

      if (preferredSize === ImageSize.Medium && image.mediumURL != null) {
        return image.mediumURL;

      } else if (preferredSize === ImageSize.Large && image.largeURL != null) {
        return image.largeURL;
      }

      return image.originalURL;
    }
    return '';
  }

  socialImageURL() {
    return this.goTourResourceHost() + '/' + this.builderPath + '/' + this.modelPath + '/images/model_fb.png';
  }

  backgroundImageStyle(image: Image, preferredSize: ImageSize) {
    return  'url(\'' + this.imageLibraryURL(image, preferredSize) + '\')';
  }

  builderModelPath() {
    return '/' + this.builderPath + '/' + this.modelPath;
  }

  builderLogoURL() {
    return this.goTourResourceHost() + '/' + this.builderPath + '/images/logo.png';
  }

  logoURLForBuilder(builder: Builder) {
    return this.goTourResourceHost() + '/' + builder.directory + '/images/logo.png';
  }

  modelImageURL() {
    return this.goTourResourceWithBuilderModelPath() + '/images/model_fb.png';
  }

  imageURLForModel(model: Model) {
    return  this.goTourResourceHost() + '/' + model.builderDirectory + '/' + model.modelDirectory + '/images/model_fb.png';
    // .optimized-md.jpg
  }

  goTourResourceWithBuilderModelPath() {
    return this.goTourResourceHost() + this.builderModelPath();
  }

  apiBaseWithBuilderModelsPath() {
    return this.goTourAPIBaseWithVersion() + '/tours/builders/' + this.builderPath + '/models/' + this.modelPath;
  }

  goTourAPIBaseWithVersion() {
    return this.goTourAPIHost() + this.goTourSettings.api_version;
  }

  goTourResourceHost() {
    return this.tourService.hostProtocol + this.goTourSettings.gotour_host;
  }

  goTourAPIHost() {
    return this.tourService.hostProtocol + this.goTourSettings.api_host;
  }

  apiHeaders() {
    return new HttpHeaders({'APIKey' : this.goTourSettings.key});
  }
}



// WEBPACK FOOTER //
// ./src/app/services/tour-api.service.ts